/**
 * Omni Commerce Hub — Amazon, Flipkart, Swiggy, Zomato (+ future external channels).
 * Separate from Zuntly Store Orders, POS, and Zuntly Marketplace (B2B).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaChartPie, FaShoppingCart, FaUndoAlt, FaBan, FaTruck, FaPlug,
  FaSync, FaChartBar, FaClipboardList, FaSearch, FaCheck, FaTimes,
  FaExternalLinkAlt, FaCopy, FaArrowLeft,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getOmniConnections,
  connectOmniChannel,
  disconnectOmniChannel,
  syncOmniNow,
  getOmniUnifiedOrders,
  getOmniDashboard,
  getOmniSyncLogs,
  getOmniAuditLogs,
  getOmniAnalytics,
  getOmniReturns,
  getOmniCancellations,
  getOmniShipping,
  performOmniOrderAction,
} from '../../../services/ClientService';

const SECTIONS = [
  { id: 'dashboard', path: '/omni-commerce', label: 'Dashboard', icon: FaChartPie },
  { id: 'orders', path: '/omni-commerce/orders', label: 'Omni Orders', icon: FaShoppingCart },
  { id: 'returns', path: '/omni-commerce/returns', label: 'Returns', icon: FaUndoAlt },
  { id: 'cancellations', path: '/omni-commerce/cancellations', label: 'Cancellations', icon: FaBan },
  { id: 'shipping', path: '/omni-commerce/shipping', label: 'Shipping', icon: FaTruck },
  { id: 'settings', path: '/omni-commerce/settings', label: 'Channel Connections', icon: FaPlug },
  { id: 'sync-logs', path: '/omni-commerce/sync-logs', label: 'Sync Logs', icon: FaSync },
  { id: 'analytics', path: '/omni-commerce/analytics', label: 'Analytics', icon: FaChartBar },
  { id: 'audit-logs', path: '/omni-commerce/audit-logs', label: 'Audit Logs', icon: FaClipboardList },
];

const BADGE_CLASSES = {
  purple: 'bg-violet-100 text-violet-700 border-violet-200',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  violet: 'bg-purple-100 text-purple-700 border-purple-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  gray: 'bg-slate-100 text-slate-700 border-slate-200',
};

const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const PlatformBadge = ({ platform, label, badge }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold border ${BADGE_CLASSES[badge] || BADGE_CLASSES.gray}`}>
    {label || platform}
  </span>
);

const copyText = async (text, label = 'Copied') => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(label);
  } catch {
    toast.info(text);
  }
};

const buildDefaultCredentials = (schema) => {
  const next = {};
  (schema?.fields || []).forEach((f) => {
    if (f.defaultValue != null) next[f.key] = f.defaultValue;
    else next[f.key] = '';
  });
  return next;
};

/** Seller connect wizard — platform-specific credentials from official docs */
const ChannelConnectWizard = ({ connection, onClose, onConnected }) => {
  const schema = connection?.connectionSchema;
  const [credentials, setCredentials] = useState(() => buildDefaultCredentials(schema));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setCredentials(buildDefaultCredentials(schema));
    setResult(null);
  }, [schema]);

  if (!schema) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <p className="text-slate-500 text-sm">No connection schema for this platform.</p>
        <button type="button" onClick={onClose} className="mt-4 text-sm font-semibold text-indigo-600">Back</button>
      </div>
    );
  }

  const setField = (key, value) => setCredentials((c) => ({ ...c, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await connectOmniChannel({
        platform: connection.platform,
        credentials,
      });
      if (res?.success) {
        toast.success(res.message || 'Seller account connected');
        setResult(res.data);
        onConnected?.(res.data);
      } else {
        toast.error(res?.message || 'Connection failed — check seller credentials');
      }
    } catch (err) {
      toast.error(err?.message || 'Connection failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="bg-white rounded-2xl border border-emerald-200 p-6 shadow-sm space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-600">Connected</p>
            <h2 className="text-xl font-bold text-slate-900 mt-1">{schema.title}</h2>
            <p className="text-sm text-slate-500 mt-1">
              Account: <span className="font-semibold text-slate-800">{result.accountLabel || connection.platform}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-slate-500 hover:text-slate-800">Done</button>
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-3">
          <p className="text-xs font-bold uppercase text-slate-500">Webhook endpoint for this seller</p>
          <p className="text-sm text-slate-600">
            Register this URL with the platform so orders land in Omni Commerce (not Zuntly Marketplace B2B).
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 break-all">
              {result.webhookUrlHint || result.webhookPath}
            </code>
            <button
              type="button"
              onClick={() => copyText(result.webhookUrlHint || result.webhookPath, 'Webhook URL copied')}
              className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-white"
              title="Copy"
            >
              <FaCopy />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
              <span className="text-slate-400">Header</span>
              <div className="font-mono font-semibold text-slate-800 mt-0.5">x-zuntly-master-id</div>
              <button
                type="button"
                className="text-indigo-600 font-semibold mt-1"
                onClick={() => copyText(result.webhookHeaders?.['x-zuntly-master-id'], 'Master ID copied')}
              >
                Copy value
              </button>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
              <span className="text-slate-400">Header</span>
              <div className="font-mono font-semibold text-slate-800 mt-0.5">x-omni-webhook-secret</div>
              <button
                type="button"
                className="text-indigo-600 font-semibold mt-1"
                onClick={() => copyText(result.webhookSecret || result.webhookHeaders?.['x-omni-webhook-secret'], 'Secret copied')}
              >
                Copy secret
              </button>
            </div>
          </div>
        </div>

        {schema.docsUrl && (
          <a
            href={schema.docsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:underline"
          >
            Official docs <FaExternalLinkAlt className="text-[10px]" />
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
      <button
        type="button"
        onClick={onClose}
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4"
      >
        <FaArrowLeft className="text-xs" /> All channels
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <PlatformBadge platform={connection.platform} label={connection.label || connection.platform} badge={connection.badge || 'gray'} />
          <h2 className="text-xl font-bold text-slate-900 mt-2">{schema.title}</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">{schema.summary}</p>
        </div>
        {schema.docsUrl && (
          <a
            href={schema.docsUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 border border-indigo-200 rounded-xl px-3 py-2 hover:bg-indigo-50"
          >
            Official docs <FaExternalLinkAlt />
          </a>
        )}
      </div>

      <ol className="mb-5 space-y-1.5 rounded-xl bg-slate-50 border border-slate-100 p-4">
        {(schema.steps || []).map((step, i) => (
          <li key={step} className="text-sm text-slate-600 flex gap-2">
            <span className="font-bold text-slate-400 shrink-0">{i + 1}.</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      {(schema.notes || []).length > 0 && (
        <ul className="mb-5 space-y-1 rounded-xl border border-amber-100 bg-amber-50 p-4">
          {schema.notes.map((n) => (
            <li key={n} className="text-xs text-amber-900">• {n}</li>
          ))}
        </ul>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(schema.fields || []).map((field) => (
            <div key={field.key} className={field.type === 'select' || field.secret ? 'md:col-span-1' : ''}>
              <label className="text-xs font-semibold text-slate-600">
                {field.label}
                {field.required ? <span className="text-red-500"> *</span> : null}
              </label>
              {field.type === 'select' ? (
                <select
                  required={field.required}
                  value={credentials[field.key] || ''}
                  onChange={(e) => setField(field.key, e.target.value)}
                  className="mt-1 w-full border border-slate-200 rounded-xl text-sm px-3 py-2.5"
                >
                  {(field.options || []).map((opt) => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.secret ? 'password' : 'text'}
                  required={field.required}
                  value={credentials[field.key] || ''}
                  onChange={(e) => setField(field.key, e.target.value)}
                  placeholder={field.placeholder || ''}
                  autoComplete="off"
                  className="mt-1 w-full border border-slate-200 rounded-xl text-sm px-3 py-2.5"
                />
              )}
              {field.help && <p className="text-[11px] text-slate-400 mt-1">{field.help}</p>}
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold disabled:opacity-60"
          >
            {submitting ? 'Verifying seller account…' : `Connect ${schema.title}`}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-bold"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const OmniCommerceHub = () => {
  const location = useLocation();
  const section = useMemo(() => {
    const match = SECTIONS.find((s) => s.path === location.pathname);
    return match?.id || 'dashboard';
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [orderMeta, setOrderMeta] = useState({ countsByPlatform: {}, countsByStatus: {}, statuses: [] });
  const [connections, setConnections] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [listRows, setListRows] = useState([]);
  const [connectTarget, setConnectTarget] = useState(null);

  const [filters, setFilters] = useState({
    platform: 'all',
    status: 'all',
    searchTerm: '',
    deliveryType: 'all',
  });

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      if (section === 'dashboard') {
        const res = await getOmniDashboard({});
        if (res?.success) setDashboard(res.data);
      } else if (section === 'orders') {
        const res = await getOmniUnifiedOrders({ ...filters, skip: 0, limit: 100 });
        if (res?.success) {
          setOrders(res.data || []);
          setOrderMeta(res.meta || {});
        }
      } else if (section === 'settings') {
        const res = await getOmniConnections({});
        if (res?.success) setConnections(res.data || []);
      } else if (section === 'sync-logs') {
        const res = await getOmniSyncLogs({ skip: 0, limit: 100 });
        if (res?.success) setSyncLogs(res.data || []);
      } else if (section === 'audit-logs') {
        const res = await getOmniAuditLogs({ skip: 0, limit: 100 });
        if (res?.success) setAuditLogs(res.data || []);
      } else if (section === 'analytics') {
        const res = await getOmniAnalytics({});
        if (res?.success) setAnalytics(res.data);
      } else if (section === 'returns') {
        const res = await getOmniReturns({});
        if (res?.success) setListRows(res.data || []);
      } else if (section === 'cancellations') {
        const res = await getOmniCancellations({});
        if (res?.success) setListRows(res.data || []);
      } else if (section === 'shipping') {
        const res = await getOmniShipping({});
        if (res?.success) setListRows(res.data || []);
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Failed to load Omni Commerce data');
    } finally {
      setLoading(false);
    }
  }, [section, filters]);

  useEffect(() => {
    loadSection();
  }, [loadSection]);

  useEffect(() => {
    setConnectTarget(null);
  }, [section]);

  const handleDisconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${platform} seller account from Omni Commerce?`)) return;
    try {
      const res = await disconnectOmniChannel({ platform });
      if (res?.success) {
        toast.success('Disconnected');
        setConnectTarget(null);
        loadSection();
      } else toast.error(res?.message || 'Disconnect failed');
    } catch (e) {
      toast.error(e?.message || 'Disconnect failed');
    }
  };

  const handleSync = async (platform) => {
    try {
      const res = await syncOmniNow({ platform, jobType: 'orders' });
      if (res?.success) {
        toast.success('Sync completed');
        loadSection();
      } else toast.error(res?.message || 'Sync failed');
    } catch (e) {
      toast.error(e?.message || 'Sync failed');
    }
  };

  const handleAction = async (order, action) => {
    if (!order.canManageInOmni) {
      toast.info('This order is managed on its own channel connection');
      return;
    }
    let reason;
    if (action === 'reject' || action === 'cancel') {
      reason = window.prompt('Enter reason:') || '';
      if (!reason) return;
    }
    try {
      const res = await performOmniOrderAction({
        orderId: order.sourceId,
        action,
        reason,
      });
      if (res?.success) {
        toast.success(res.message || 'Updated');
        loadSection();
      } else toast.error(res?.message || 'Action failed');
    } catch (e) {
      toast.error(e?.message || 'Action failed');
    }
  };

  const today = dashboard?.today || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1400px] mx-auto p-4 md:p-6">
        <div className="mb-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-indigo-600">Sales</p>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">Omni Commerce</h1>
          <p className="text-sm text-slate-500 mt-1">
            Amazon, Flipkart, Swiggy &amp; Zomato seller channels — separate from Zuntly Store, POS, and Zuntly Marketplace (B2B).
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-5">
          <aside className="lg:w-56 shrink-0">
            <nav className="bg-white rounded-2xl border border-slate-200 shadow-sm p-2 space-y-0.5 sticky top-4">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                const active = section === s.id;
                return (
                  <Link
                    key={s.id}
                    to={s.path}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition ${
                      active
                        ? 'bg-indigo-600 text-white shadow-md'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <Icon className="text-sm opacity-90" />
                    {s.label}
                  </Link>
                );
              })}
              <Link
                to="/orders"
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition mt-2 border-t border-slate-100 pt-3"
              >
                Zuntly Store Orders →
              </Link>
            </nav>
          </aside>

          <main className="flex-1 min-w-0">
            {loading ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-400">
                Loading…
              </div>
            ) : (
              <>
                {section === 'dashboard' && (
                  <div className="space-y-4">
                    {dashboard?.note && (
                      <div className="bg-sky-50 border border-sky-200 text-sky-900 text-sm rounded-2xl p-4">
                        {dashboard.note}
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: "Today's Channel Orders", value: today.orders || 0 },
                        { label: "Today's Revenue", value: formatINR(today.revenue) },
                        { label: 'Pending', value: today.pending || 0 },
                        { label: 'Delivered', value: today.delivered || 0 },
                        { label: 'Accepted', value: today.accepted || 0 },
                        { label: 'Cancelled', value: today.cancelled || 0 },
                        { label: 'Packing', value: today.packing || 0 },
                        { label: 'Out For Delivery', value: today.outForDelivery || 0 },
                      ].map((c) => (
                        <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{c.label}</p>
                          <p className="text-xl font-bold text-slate-900 mt-1">{c.value}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                      <h2 className="font-bold text-slate-800 mb-3">Channel-wise (Today)</h2>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {Object.entries(dashboard?.platformWise || {}).map(([key, val]) => (
                          <div key={key} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
                            <p className="text-xs font-bold uppercase text-slate-500">{key}</p>
                            <p className="text-lg font-bold text-slate-900">{val.orders} orders</p>
                            <p className="text-sm text-indigo-600 font-semibold">{formatINR(val.revenue)}</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 mt-4">
                        Seller accounts connected: {dashboard?.connections?.connected || 0}/{dashboard?.connections?.total || 0}
                      </p>
                      {(dashboard?.connections?.accounts || []).length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {dashboard.connections.accounts.map((a) => (
                            <li key={a.platform} className="text-xs text-slate-600">
                              <span className="font-semibold capitalize">{a.platform}</span>
                              {a.accountLabel ? ` · ${a.accountLabel}` : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {section === 'orders' && (
                  <div className="space-y-4">
                    <div className="bg-slate-100 border border-slate-200 text-slate-700 text-sm rounded-2xl p-4">
                      Omni Orders show Amazon / Flipkart / Swiggy / Zomato only. Zuntly Store, POS, and Zuntly Marketplace (B2B) stay in their own modules.
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-wrap gap-3 items-end">
                      <div className="flex-1 min-w-[180px]">
                        <label className="text-xs font-semibold text-slate-500">Search</label>
                        <div className="relative mt-1">
                          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                          <input
                            value={filters.searchTerm}
                            onChange={(e) => setFilters((f) => ({ ...f, searchTerm: e.target.value }))}
                            placeholder="Order, customer, phone, SKU…"
                            className="w-full pl-9 pr-3 py-2 border rounded-xl text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500">Platform</label>
                        <select
                          value={filters.platform}
                          onChange={(e) => setFilters((f) => ({ ...f, platform: e.target.value }))}
                          className="mt-1 block border rounded-xl text-sm px-3 py-2"
                        >
                          <option value="all">All channels</option>
                          <option value="amazon">Amazon</option>
                          <option value="flipkart">Flipkart</option>
                          <option value="swiggy">Swiggy</option>
                          <option value="zomato">Zomato</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500">Status</label>
                        <select
                          value={filters.status}
                          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                          className="mt-1 block border rounded-xl text-sm px-3 py-2"
                        >
                          <option value="all">All</option>
                          {(orderMeta.statuses || []).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={loadSection}
                        className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold"
                      >
                        Apply
                      </button>
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-4 py-3">Platform</th>
                              <th className="px-4 py-3">Order</th>
                              <th className="px-4 py-3">Customer</th>
                              <th className="px-4 py-3">Items</th>
                              <th className="px-4 py-3">Amount</th>
                              <th className="px-4 py-3">Payment</th>
                              <th className="px-4 py-3">Status</th>
                              <th className="px-4 py-3">Branch</th>
                              <th className="px-4 py-3">Created</th>
                              <th className="px-4 py-3">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.length === 0 ? (
                              <tr>
                                <td colSpan={10} className="px-4 py-12 text-center text-slate-400">
                                  No channel orders yet — connect a seller account under Channel Connections
                                </td>
                              </tr>
                            ) : orders.map((o) => (
                              <tr key={o.unifiedId} className="border-t border-slate-100 hover:bg-slate-50/80">
                                <td className="px-4 py-3">
                                  <PlatformBadge platform={o.platform} label={o.platformLabel} badge={o.badge} />
                                </td>
                                <td className="px-4 py-3 font-semibold text-slate-800">
                                  {o.orderNumber}
                                  {o.externalOrderId && (
                                    <div className="text-[10px] text-slate-400 font-normal">{o.externalOrderId}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="font-medium text-slate-800">{o.customer?.name || '—'}</div>
                                  <div className="text-xs text-slate-400">{o.customer?.phone}</div>
                                </td>
                                <td className="px-4 py-3 text-slate-600">{o.itemsCount}</td>
                                <td className="px-4 py-3 font-semibold">{formatINR(o.amount)}</td>
                                <td className="px-4 py-3">{o.payment}</td>
                                <td className="px-4 py-3">
                                  <div className="font-semibold text-slate-800">{o.status}</div>
                                  {o.marketplaceStatus && o.marketplaceStatus !== o.status && (
                                    <div className="text-[10px] text-slate-400">Channel: {o.marketplaceStatus}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-slate-600">{o.branchName || '—'}</td>
                                <td className="px-4 py-3 text-xs text-slate-500">
                                  {o.createdAt ? new Date(o.createdAt).toLocaleString('en-IN') : '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap gap-1">
                                    {o.canManageInOmni ? (
                                      <>
                                        <button type="button" onClick={() => handleAction(o, 'accept')} className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-[11px] font-bold"><FaCheck className="inline mr-1" />Accept</button>
                                        <button type="button" onClick={() => handleAction(o, 'reject')} className="px-2 py-1 rounded-lg bg-red-50 text-red-700 text-[11px] font-bold"><FaTimes className="inline mr-1" />Reject</button>
                                      </>
                                    ) : (
                                      <span className="text-[11px] text-slate-400">—</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {section === 'settings' && (
                  <div className="space-y-4">
                    {connectTarget ? (
                      <ChannelConnectWizard
                        connection={connectTarget}
                        onClose={() => setConnectTarget(null)}
                        onConnected={() => loadSection()}
                      />
                    ) : (
                      <>
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
                          <h2 className="text-lg font-bold text-slate-900">Channel Connections</h2>
                          <p className="text-sm text-slate-500 mt-1">
                            Link your <span className="font-semibold text-slate-700">real seller / restaurant accounts</span> on Amazon, Flipkart, Swiggy, and Zomato.
                            This is not Zuntly Marketplace (B2B) — that stays under Marketplace Settings elsewhere.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {connections.map((c) => {
                            const schema = c.connectionSchema;
                            const connected = c.status === 'connected';
                            return (
                              <div key={c.platform} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex flex-col">
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <PlatformBadge platform={c.platform} label={c.label || c.platform} badge={c.badge || 'gray'} />
                                    <h3 className="text-lg font-bold text-slate-900 mt-2">
                                      {schema?.title || c.label || c.platform}
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                      {schema?.summary || 'Connect seller credentials for this channel.'}
                                    </p>
                                  </div>
                                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full border ${
                                    connected
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                      : 'bg-slate-50 text-slate-500 border-slate-200'
                                  }`}>
                                    {connected ? 'Connected' : 'Not connected'}
                                  </span>
                                </div>
                                {connected && (
                                  <div className="mt-3 text-xs text-slate-500 space-y-0.5">
                                    {c.accountLabel && (
                                      <p>Seller / outlet: <span className="font-semibold text-slate-800">{c.accountLabel}</span></p>
                                    )}
                                    <p>Last sync: {c.lastSyncAt ? new Date(c.lastSyncAt).toLocaleString('en-IN') : 'Never'}</p>
                                    {c.healthStatus && <p>Health: <span className="font-semibold">{c.healthStatus}</span></p>}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-2 mt-auto pt-4">
                                  {connected ? (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => setConnectTarget(c)}
                                        className="px-3 py-2 rounded-xl border border-indigo-200 text-indigo-700 text-xs font-bold"
                                      >
                                        Reconnect / Update
                                      </button>
                                      <button type="button" onClick={() => handleSync(c.platform)} className="px-3 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold">Sync Now</button>
                                      <button type="button" onClick={() => handleDisconnect(c.platform)} className="px-3 py-2 rounded-xl border border-slate-200 text-slate-700 text-xs font-bold">Disconnect</button>
                                    </>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setConnectTarget(c)}
                                      className="px-3 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold"
                                    >
                                      Connect seller account
                                    </button>
                                  )}
                                  {schema?.docsUrl && (
                                    <a
                                      href={schema.docsUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="px-3 py-2 rounded-xl text-xs font-bold text-slate-500 hover:text-indigo-600 inline-flex items-center gap-1"
                                    >
                                      Docs <FaExternalLinkAlt className="text-[9px]" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {(section === 'returns' || section === 'cancellations' || section === 'shipping') && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800 capitalize">{section}</div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
                          <tr>
                            <th className="px-4 py-3">Platform</th>
                            <th className="px-4 py-3">Order</th>
                            <th className="px-4 py-3">Customer</th>
                            <th className="px-4 py-3">Amount</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {listRows.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No channel records yet</td></tr>
                          ) : listRows.map((o) => (
                            <tr key={o.unifiedId} className="border-t border-slate-100">
                              <td className="px-4 py-3"><PlatformBadge platform={o.platform} label={o.platformLabel} badge={o.badge} /></td>
                              <td className="px-4 py-3 font-semibold">{o.orderNumber}</td>
                              <td className="px-4 py-3">{o.customer?.name}</td>
                              <td className="px-4 py-3">{formatINR(o.amount)}</td>
                              <td className="px-4 py-3">{o.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {section === 'sync-logs' && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
                        <tr>
                          <th className="px-4 py-3">Time</th>
                          <th className="px-4 py-3">Platform</th>
                          <th className="px-4 py-3">Job</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {syncLogs.length === 0 ? (
                          <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No sync logs yet</td></tr>
                        ) : syncLogs.map((l) => (
                          <tr key={l._id} className="border-t border-slate-100">
                            <td className="px-4 py-3 text-xs">{l.createdAt ? new Date(l.createdAt).toLocaleString('en-IN') : '—'}</td>
                            <td className="px-4 py-3 capitalize">{l.platform}</td>
                            <td className="px-4 py-3">{l.jobType}</td>
                            <td className="px-4 py-3 font-semibold">{l.status}</td>
                            <td className="px-4 py-3 text-slate-600">{l.message || l.error || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {section === 'audit-logs' && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
                        <tr>
                          <th className="px-4 py-3">Time</th>
                          <th className="px-4 py-3">Actor</th>
                          <th className="px-4 py-3">Action</th>
                          <th className="px-4 py-3">Platform</th>
                          <th className="px-4 py-3">Entity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLogs.length === 0 ? (
                          <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No audit events yet</td></tr>
                        ) : auditLogs.map((l) => (
                          <tr key={l._id} className="border-t border-slate-100">
                            <td className="px-4 py-3 text-xs">{l.createdAt ? new Date(l.createdAt).toLocaleString('en-IN') : '—'}</td>
                            <td className="px-4 py-3">{l.actor?.name || l.actor?.email || '—'}</td>
                            <td className="px-4 py-3 font-semibold">{l.action}</td>
                            <td className="px-4 py-3 capitalize">{l.platform || '—'}</td>
                            <td className="px-4 py-3 text-xs text-slate-500">{l.entityType} {l.entityId ? String(l.entityId).slice(-6) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {section === 'analytics' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Acceptance Rate', value: `${analytics?.kpis?.acceptanceRate ?? 0}%` },
                        { label: 'Cancellation Rate', value: `${analytics?.kpis?.cancellationRate ?? 0}%` },
                        { label: 'AOV', value: formatINR(analytics?.kpis?.averageOrderValue) },
                        { label: 'Revenue Today', value: formatINR(analytics?.today?.revenue) },
                      ].map((c) => (
                        <div key={c.label} className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">{c.label}</p>
                          <p className="text-xl font-bold text-slate-900 mt-1">{c.value}</p>
                        </div>
                      ))}
                    </div>
                    {analytics?.note && (
                      <p className="text-xs text-slate-400 bg-white border border-slate-200 rounded-xl p-3">{analytics.note}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default OmniCommerceHub;
export { SECTIONS as OMNI_COMMERCE_SECTIONS };
